import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const ADMIN_EMAIL = 'sharmadeepika85.ds@gmail.com';

// Normalize user from PHP (id → _id, full_name → name)
const normalizeUser = (u) => u ? ({
  ...u,
  _id:  String(u._id  || u.id  || ''),
  id:   u.id   || u._id,
  name: u.name || u.full_name || '',
}) : null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mbx_token'));
  const [loading, setLoading] = useState(true);

  // Attach token to all axios requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const verifyToken = useCallback(async (t) => {
    try {
      const decoded = jwtDecode(t);
      if (decoded.exp * 1000 < Date.now()) {
        logout();
        return;
      }
      const { data } = await axios.get('/api/me.php');
      setUser(normalizeUser(data.user));
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  const login = async (email, password) => {
    const { data } = await axios.post('/api/login.php', { email, password });
    if (data.error) throw new Error(data.error);
    localStorage.setItem('mbx_token', data.token);
    setToken(data.token);
    setUser(normalizeUser(data.user));
    return data;
  };

  const register = async (formData) => {
    const { data } = await axios.post('/api/register.php', formData);
    if (data.error) throw new Error(data.error);
    localStorage.setItem('mbx_token', data.token);
    setToken(data.token);
    setUser(normalizeUser(data.user));
    return data;
  };

  const loginWithGoogle = async (googleToken) => {
    const { data } = await axios.post('/api/google.php', { token: googleToken });
    localStorage.setItem('mbx_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const verifyOtp = async (email, otp) => {
    const { data } = await axios.post('/api/verify_otp.php', { email, otp });
    if (data.token) {
      localStorage.setItem('mbx_token', data.token);
      setToken(data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = () => {
    localStorage.removeItem('mbx_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const isAdmin = !!(user && (user.role === 'admin' || user.email === ADMIN_EMAIL));

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, verifyOtp, logout, updateUser, isAuthenticated: !!user, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
